import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize, TextType} from '@Components/text';
import {VersionHistoryListItem} from '@Components/version-history-list/components/version-history-list-item';
import {VersionHistoryListSkeleton} from '@Components/version-history-list/components/version-history-list-skeleton';
import {NEW_SUBSCRIPTION_START_SOURCE_COOKIE, NewSubscriptionStartSource} from '@Libraries/ga-events';
import {isUserPAYG} from '@Libraries/user.library';
import {openPremiumPricingModal} from '@Modals/premium-pricing-modal/premium-pricing-modal.library';
import {COOKIE_EXPIRY_30_MINUTES, setCookie} from '@Utils/cookie.util';
import type {VersionHistoryListProps} from './version-history-list.types';
import styles from './version-history-list.module.scss';

function VersionHistoryList({versions, selectedIndex, isFetchingVersions, loadingIndex = null, onVersionSelect}: VersionHistoryListProps): ReactElement {
  const renderPremiumNotice = (): ReactElement | null => {
    if (!isUserPAYG()) {
      return null;
    }

    return (
      <div className={`flexbox flex-items-center gap-0 ${styles.premiumNotice}`}>
        <div>
          <Text val={window.i18next.t('pmwjs_version_history_limited')} type={TextType.BODY} size={TextSize.XSMALL} bold />
          <br />
          <Text val={window.i18next.t('pmwjs_unlock_premium_version_history')} type={TextType.BODY} size={TextSize.XSMALL} />
          <Text
            val={window.i18next.t('pmwjs_upgrade')}
            className={styles.upgradeLink}
            type={TextType.BODY}
            size={TextSize.XSMALL}
            underline
            onClick={(): void => {
              setCookie(NEW_SUBSCRIPTION_START_SOURCE_COOKIE, NewSubscriptionStartSource.VERSION_HISTORY_UPGRADE_BUTTON, COOKIE_EXPIRY_30_MINUTES);
              openPremiumPricingModal(false);
            }}
          />
        </div>
      </div>
    );
  };

  const renderListContent = (): ReactElement | ReactElement[] => {
    if (isFetchingVersions) {
      return <VersionHistoryListSkeleton />;
    }

    return versions.map((version, index) => (
      <VersionHistoryListItem
        key={version.id}
        version={version}
        index={index}
        isSelected={index === selectedIndex}
        isLoading={loadingIndex === index}
        onVersionSelect={onVersionSelect}
      />
    ));
  };

  return (
    <>
      {renderPremiumNotice()}
      <ul className="flexbox flex-column-justify-start gap-2">
        {renderListContent()}
      </ul>
    </>
  );
}

export default React.memo(VersionHistoryList);
